import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, CalendarClock, Search, CheckCircle2, Circle, ClipboardCheck, AlertTriangle, Users, Piano, Music2, Guitar, Lock, Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { API, fmtDate, calcAge, norm } from '@/lib/helpers';

export default function PresencaTab({ events, pessoas, auxiliares, tiposEvento, user, fetchNotifications, onEventUpdate }) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const [quickMode, setQuickMode] = useState(true);
  const [absentLast5, setAbsentLast5] = useState([]);
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeForm, setCloseForm] = useState({ quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '' });
  const [mySchedule, setMySchedule] = useState(null);
  const [serviceFunctions, setServiceFunctions] = useState([]);

  // Find the logged-in user's pessoa profile
  const myPessoa = useMemo(() => {
    if (!user || !pessoas) return null;
    if (user.pessoa_id) return pessoas.find(p => p.pessoa_id === user.pessoa_id) || null;
    if (user.email) return pessoas.find(p => p.email === user.email) || null;
    return null;
  }, [user, pessoas]);

  useEffect(() => {
    if (!selectedEvent && events && events.length > 0) {
      const openEvts = events.filter(e => e.status === 'aberto');
      if (openEvts.length > 0) {
        openEvts.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSelectedEvent(openEvts[0]);
      }
    }
  }, [events]);

  useEffect(() => {
    axios.get(`${API}/attendance/absent-last-5`, { withCredentials: true }).then(r => setAbsentLast5(r.data)).catch(() => {});
  }, [attendance]);

  // Fetch service schedule and functions for the selected event
  useEffect(() => {
    if (!selectedEvent) { setMySchedule(null); return; }
    axios.get(`${API}/service-schedules/${selectedEvent.event_id}`, { withCredentials: true })
      .then(r => setMySchedule(r.data))
      .catch(() => setMySchedule(null));
  }, [selectedEvent]);

  useEffect(() => {
    axios.get(`${API}/service-functions`, { withCredentials: true })
      .then(r => setServiceFunctions(r.data || []))
      .catch(() => {});
  }, []);

  const openEvents = events.filter(e => e.status === 'aberto').sort((a, b) => new Date(a.date) - new Date(b.date));

  useEffect(() => {
    if (selectedEvent) fetchAttendance(selectedEvent.event_id);
  }, [selectedEvent]);

  const fetchAttendance = async (eventId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/attendance/event/${eventId}`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); const m = {}; d.forEach(a => { m[a.pessoa_id] = a.presente; }); setAttendance(m); }
    } catch {} finally { setLoading(false); }
  };

  const handleToggle = async (pessoaId, current) => {
    if (!selectedEvent) return;
    try {
      const r = await fetch(`${API}/attendance`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ event_id: selectedEvent.event_id, pessoa_id: pessoaId, presente: !current }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      setAttendance(prev => ({ ...prev, [pessoaId]: !current }));
      toast.success(!current ? 'Presença marcada!' : 'Ausência marcada!');
      if (quickMode) {
        setSearch('');
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    } catch (err) { toast.error(err.message); }
  };

  // My function assignments for the selected event
  const myFunctions = useMemo(() => {
    if (!myPessoa || !mySchedule?.assignments) return [];
    const pid = myPessoa.pessoa_id;
    return (mySchedule.assignments || [])
      .filter(a => (a.male || []).includes(pid) || (a.female || []).includes(pid))
      .map(a => {
        const sf = serviceFunctions.find(f => f.function_id === a.function_id);
        return sf?.nome || a.function_name || a.function_id;
      });
  }, [myPessoa, mySchedule, serviceFunctions]);

  // My presence status
  const myPresenceStatus = useMemo(() => {
    if (!myPessoa) return null;
    const val = attendance[myPessoa.pessoa_id];
    if (val === true) return 'presente';
    if (val === false) return 'ausente';
    return 'aguardando';
  }, [myPessoa, attendance]);

  // Close Event logic
  const selectedTipoClose = (tiposEvento || []).find(t => t.tipo_evento_id === selectedEvent?.tipo_evento_id);

  const getCloseDeadlineInfo = () => {
    if (!selectedEvent || !selectedTipoClose?.fechamento_valor || !selectedEvent.date) return { canClose: true, label: '' };
    try {
      const eventDate = new Date(selectedEvent.date);
      if (isNaN(eventDate.getTime())) return { canClose: true, label: '' };
      const delta = selectedTipoClose.fechamento_unidade === 'dias' ? selectedTipoClose.fechamento_valor * 24 * 60 * 60 * 1000 : selectedTipoClose.fechamento_valor * 60 * 60 * 1000;
      const deadline = new Date(eventDate.getTime() + delta);
      const now = new Date();
      if (now >= deadline) return { canClose: true, label: '' };
      const diffMs = deadline - now;
      const diffH = Math.ceil(diffMs / (1000 * 60 * 60));
      const label = diffH >= 24 ? `Podera ser fechado em ${Math.ceil(diffH / 24)} dia(s)` : `Podera ser fechado em ${diffH} hora(s)`;
      return { canClose: false, label };
    } catch { return { canClose: true, label: '' }; }
  };

  const openCloseDialog = () => {
    const cf = {
      quem_presidiu: selectedEvent?.quem_presidiu || '',
      qtd_musicos: selectedEvent?.qtd_musicos ?? '',
      qtd_ministerio: selectedEvent?.qtd_ministerio ?? '',
      qtd_participantes: selectedEvent?.qtd_participantes ?? '',
      recitativos: { individual_meninos: '', individual_meninas: '' },
    };
    if (selectedTipoClose?.tem_recitativos) {
      const rec = selectedEvent?.recitativos || {};
      cf.recitativos = { individual_meninos: rec.individual_meninos ?? '', individual_meninas: rec.individual_meninas ?? '' };
    }
    setCloseForm(cf);
    setCloseDialog(true);
  };

  const buildCloseRecitativosPayload = () => {
    const rec = closeForm.recitativos || {};
    return { individual_meninos: rec.individual_meninos !== '' ? parseInt(rec.individual_meninos) : null, individual_meninas: rec.individual_meninas !== '' ? parseInt(rec.individual_meninas) : null };
  };

  const handleCloseEventSubmit = async () => {
    const missing = [];
    if (selectedTipoClose?.tem_presidencia && !(closeForm.quem_presidiu || '').trim()) missing.push('Quem Presidiu');
    if (selectedTipoClose?.tem_musicos && (closeForm.qtd_musicos === '' || closeForm.qtd_musicos === null)) missing.push('Qtd. de Músicos');
    if (selectedTipoClose?.tem_ministerio && (closeForm.qtd_ministerio === '' || closeForm.qtd_ministerio === null)) missing.push('Qtd. de Ministério');
    if (selectedTipoClose?.tem_participantes && (closeForm.qtd_participantes === '' || closeForm.qtd_participantes === null)) missing.push('Qtd. de Participantes');
    if (missing.length > 0) { toast.error(`Preencha: ${missing.join(', ')}`); return; }
    try {
      const payload = {
        ...selectedEvent,
        status: 'fechado',
        quem_presidiu: closeForm.quem_presidiu,
        qtd_musicos: closeForm.qtd_musicos !== '' ? parseInt(closeForm.qtd_musicos) : null,
        qtd_ministerio: closeForm.qtd_ministerio !== '' ? parseInt(closeForm.qtd_ministerio) : null,
        qtd_participantes: closeForm.qtd_participantes !== '' ? parseInt(closeForm.qtd_participantes) : null,
        recitativos: selectedTipoClose?.tem_recitativos ? buildCloseRecitativosPayload() : null,
      };
      const res = await fetch(`${API}/events/${selectedEvent.event_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao fechar evento');
      const updated = await res.json();
      setSelectedEvent(updated);
      setCloseDialog(false);
      toast.success('Evento fechado!');
      if (onEventUpdate) onEventUpdate(updated);
      if (fetchNotifications) fetchNotifications();
    } catch (error) { toast.error(error.message); }
  };

  const pessoaIdSet = new Set(pessoas.map(p => p.pessoa_id));
  const presentCount = selectedEvent ? Object.entries(attendance).filter(([pid, v]) => v && pessoaIdSet.has(pid)).length : 0;
  const getAvatarColor = (p) => p.genero === 'feminino' ? '#EC4899' : '#3B82F6';

  const filteredPessoas = pessoas.filter(p => {
    if (search && !norm(p.nome_completo).includes(norm(search))) return false;
    return true;
  });

  const isFutureEvent = selectedEvent && new Date(selectedEvent.date) > new Date();

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
      {/* Events sidebar */}
      <Card className="bg-white border-0 shadow-sm lg:col-span-1">
        <CardHeader className="pb-3 sm:pb-4 px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Presença</h2>
                <Badge className="bg-indigo-100 text-indigo-700 text-sm font-bold">{openEvents.length}</Badge>
              </div>
              <p className="text-xs sm:text-sm text-slate-500">Em aberto</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <div className="space-y-2" data-testid="attendance-events">
            {openEvents.map((ev) => {
              const evIsFuture = new Date(ev.date) > new Date();
              return (
              <button key={ev.event_id} onClick={() => { setSelectedEvent(ev); setSearch(''); }} data-testid={`att-event-${ev.event_id}`}
                className={`w-full text-left p-2.5 sm:p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-2 sm:gap-3 ${selectedEvent?.event_id === ev.event_id ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${evIsFuture ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                  {evIsFuture ? (
                    <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span><CalendarClock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" /></span></TooltipTrigger><TooltipContent><p className="text-xs">Evento futuro</p></TooltipContent></Tooltip></TooltipProvider>
                  ) : <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-600" />}
                </div>
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-semibold text-sm">{ev.name}</h3>
                  </div>
                  <p className="text-xs text-slate-500">{fmtDate(ev.date)}</p>
                </div>
              </button>
              );
            })}
            {openEvents.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Nenhum evento aberto</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main content area */}
      <div className={`lg:col-span-3 ${selectedEvent ? 'space-y-4' : ''}`}>

        {/* Stats cards */}
        {selectedEvent && (() => {
          const presentIds = Object.entries(attendance).filter(([,v]) => v).map(([k]) => k);
          const presentPessoas = pessoas.filter(p => presentIds.includes(p.pessoa_id));
          const organistas = presentPessoas.filter(p => p.organista).length;
          const musicos = presentPessoas.filter(p => p.musico).length;
          const violeiros = presentPessoas.filter(p => p.musico && p.instrumentos?.includes('Violão')).length;
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Presentes</p>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{presentCount}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Organistas</p>
                  <div className="flex items-center gap-2">
                    <Piano className="w-5 h-5 text-orange-500" />
                    <p className="text-xl sm:text-2xl font-bold text-orange-500">{organistas}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Músicos</p>
                  <div className="flex items-center gap-2">
                    <Music2 className="w-5 h-5 text-red-600" />
                    <p className="text-xl sm:text-2xl font-bold text-red-600">{musicos}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Violeiros</p>
                  <div className="flex items-center gap-2">
                    <Guitar className="w-5 h-5 text-blue-600" />
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">{violeiros}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Event Header Card (separated) */}
        {selectedEvent && (
          <Card className="bg-white border-0 shadow-sm" data-testid="presenca-event-header">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isFutureEvent ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                    {isFutureEvent ? <CalendarClock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" /> : <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        {selectedEvent.name}
                      </h2>
                      {(() => { const t = (tiposEvento || []).find(t => t.tipo_evento_id === selectedEvent.tipo_evento_id); return t ? <Badge variant="outline" className="text-xs font-medium" data-testid="badge-tipo-selected">{t.nome}</Badge> : null; })()}
                      {isFutureEvent && <Badge variant="outline" className="text-xs font-medium" data-testid="badge-evento-futuro">Evento Futuro</Badge>}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500">{fmtDate(selectedEvent.date)}</p>
                  </div>
                </div>

                {/* Actions: Close button */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedEvent.status !== 'fechado' && (() => {
                    const { canClose, label } = getCloseDeadlineInfo();
                    return canClose ? (
                      <Button size="sm" variant="outline" onClick={openCloseDialog} className="text-orange-600 border-orange-300 hover:bg-orange-50 text-xs sm:text-sm" data-testid="admin-close-event-btn">
                        <Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Fechar</span>
                      </Button>
                    ) : (
                      <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span>
                        <Button size="sm" variant="outline" disabled className="text-slate-400 border-slate-200 cursor-not-allowed text-xs sm:text-sm" data-testid="admin-close-event-btn-disabled">
                          <Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Fechar</span>
                        </Button>
                      </span></TooltipTrigger><TooltipContent><p className="text-xs">{label}</p></TooltipContent></Tooltip></TooltipProvider>
                    );
                  })()}
                  {selectedEvent.status === 'fechado' && (
                    <Badge className="bg-orange-100 text-orange-700 text-xs font-semibold">Fechado</Badge>
                  )}
                </div>
              </div>

              {/* Logged-in user's presence status and function badges */}
              {myPessoa && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid="presenca-my-status">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: getAvatarColor(myPessoa) }}>
                    {myPessoa.nome_completo?.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-slate-700">{myPessoa.apelido || myPessoa.nome_completo?.split(' ')[0]}</span>
                  <Badge
                    data-testid="presenca-my-status-badge"
                    className={`text-[10px] font-semibold ${
                      myPresenceStatus === 'presente' ? 'bg-green-100 text-green-700' :
                      myPresenceStatus === 'ausente' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {myPresenceStatus === 'presente' ? 'Presente' : myPresenceStatus === 'ausente' ? 'Ausente' : 'Aguardando'}
                  </Badge>
                  {myFunctions.map((fname, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
                      {fname}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* People list Card (filter + list only) */}
        <Card className="bg-white border-0 shadow-sm">
          {selectedEvent && (
            <CardHeader className="pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" placeholder="Buscar..." data-testid="admin-attendance-search" />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={() => setQuickMode(!quickMode)} data-testid="quick-mode-toggle"
                        className={`flex items-center gap-1.5 px-3 h-9 rounded-md border-2 transition-all text-xs font-semibold flex-shrink-0 ${quickMode ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
                        <Zap className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Rápida</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Marcação rápida: limpa a busca e foca no campo após marcar presença</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
          )}
          <CardContent className="px-3 sm:px-6">
            {loading ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
            ) : selectedEvent ? (
              <div className="space-y-0">
                {filteredPessoas.map((p) => (
                  <div key={p.pessoa_id} className={`px-3 sm:px-4 py-3 border-b last:border-b-0 transition-all ${attendance[p.pessoa_id] ? 'border-green-100 bg-green-50/30' : 'border-slate-100'}`}>
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 text-white" style={{ backgroundColor: getAvatarColor(p) }}>{p.nome_completo.charAt(0)}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
                            {absentLast5.includes(p.pessoa_id) && (
                              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" /></TooltipTrigger><TooltipContent><p>Ausente nos últimos 5 eventos</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            <Badge variant="outline" className="text-xs">{calcAge(p.data_nascimento)}</Badge>
                            {p.organista && <Badge variant="outline" className="text-xs" title="Organista"><Piano className="w-3.5 h-3.5" /></Badge>}
                            {p.musico && <Badge variant="outline" className="text-xs" title="Músico"><Music2 className="w-3.5 h-3.5" /></Badge>}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleToggle(p.pessoa_id, attendance[p.pessoa_id])} data-testid={`toggle-att-${p.pessoa_id}`}
                        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-shrink-0 ${attendance[p.pessoa_id] ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                        {attendance[p.pessoa_id] ? <><CheckCircle2 className="w-4 h-4" /><span className="hidden sm:inline">Presente</span></> : <><Circle className="w-4 h-4" /><span className="hidden sm:inline">Ausente</span></>}
                      </button>
                    </div>
                  </div>
                ))}
                {filteredPessoas.length === 0 && (
                  <div className="text-center py-8">
                    <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Nenhum resultado encontrado</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Selecione um evento para marcar presença</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Close Event Dialog */}
    <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Fechar Evento</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600 mb-2">
            Ao fechar o evento <strong>{selectedEvent?.name}</strong>, a presença será finalizada.
          </p>
          {selectedTipoClose && (selectedTipoClose.tem_presidencia || selectedTipoClose.tem_participantes || selectedTipoClose.tem_musicos || selectedTipoClose.tem_ministerio) && (
            <div className="space-y-4 border border-slate-200 rounded-lg p-4">
              <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Informações Adicionais</span>
              {selectedTipoClose.tem_presidencia && (
                <div><Label className="text-xs text-slate-500">Quem Presidiu? *</Label><Input value={closeForm.quem_presidiu} onChange={(e) => setCloseForm({ ...closeForm, quem_presidiu: e.target.value })} data-testid="admin-pres-close-quem-presidiu" /></div>
              )}
              {(selectedTipoClose.tem_musicos || selectedTipoClose.tem_ministerio || selectedTipoClose.tem_participantes) && (
                <div className={`grid gap-3 ${[selectedTipoClose.tem_musicos, selectedTipoClose.tem_ministerio, selectedTipoClose.tem_participantes].filter(Boolean).length >= 3 ? 'grid-cols-3' : [selectedTipoClose.tem_musicos, selectedTipoClose.tem_ministerio, selectedTipoClose.tem_participantes].filter(Boolean).length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {selectedTipoClose.tem_musicos && (
                    <div><Label className="text-xs text-slate-500">Qtd. de Músicos *</Label><Input type="number" min="0" value={closeForm.qtd_musicos} onChange={(e) => setCloseForm({ ...closeForm, qtd_musicos: e.target.value })} data-testid="admin-pres-close-qtd-musicos" /></div>
                  )}
                  {selectedTipoClose.tem_ministerio && (
                    <div><Label className="text-xs text-slate-500">Qtd. de Ministério *</Label><Input type="number" min="0" value={closeForm.qtd_ministerio} onChange={(e) => setCloseForm({ ...closeForm, qtd_ministerio: e.target.value })} data-testid="admin-pres-close-qtd-ministerio" /></div>
                  )}
                  {selectedTipoClose.tem_participantes && (
                    <div><Label className="text-xs text-slate-500">Qtd. de Participantes *</Label><Input type="number" min="0" value={closeForm.qtd_participantes} onChange={(e) => setCloseForm({ ...closeForm, qtd_participantes: e.target.value })} data-testid="admin-pres-close-qtd-participantes" /></div>
                  )}
                </div>
              )}
            </div>
          )}
          {selectedTipoClose?.tem_recitativos && (
            <div className="space-y-3 border border-slate-200 rounded-lg p-4">
              <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Contagem de Recitativos</span>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <span className="block text-center text-xs font-semibold text-pink-700 bg-pink-50 border border-pink-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Meninas</span>
                  <div><Label className="text-xs text-slate-500">Individual</Label><Input type="number" min="0" value={closeForm.recitativos?.individual_meninas ?? ''} onChange={(e) => setCloseForm(prev => ({ ...prev, recitativos: { ...prev.recitativos, individual_meninas: e.target.value } }))} /></div>
                </div>
                <div className="space-y-3">
                  <span className="block text-center text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Meninos</span>
                  <div><Label className="text-xs text-slate-500">Individual</Label><Input type="number" min="0" value={closeForm.recitativos?.individual_meninos ?? ''} onChange={(e) => setCloseForm(prev => ({ ...prev, recitativos: { ...prev.recitativos, individual_meninos: e.target.value } }))} /></div>
                </div>
              </div>
            </div>
          )}
          <Button onClick={handleCloseEventSubmit} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="admin-pres-close-event-confirm">
            <Lock className="w-4 h-4 mr-1.5" />Fechar Evento
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
